import type { CameraPosition } from './Camera/types';
import { isModelViewerModelId } from './ModelVariant';
import {
    DEFAULT_MODEL_VIEWER_POST_EFFECTS,
    resolveModelViewerPostEffectsData,
    type ModelViewerPreviewPostEffectsData,
} from './PostEffects/config';
import {
    DEFAULT_MODEL_VIEWER_PEDESTAL,
    DEFAULT_MODEL_VIEWER_PEDESTAL_MATERIAL,
    isModelViewerPedestal,
    isModelViewerPedestalMaterial,
    type ModelViewerPedestal,
    type ModelViewerPedestalMaterial,
} from './Presentation/Pedestal';
import {
    DEFAULT_MODEL_VIEWER_CAMERA_FOV,
    DEFAULT_MODEL_VIEWER_CYCLORAMA_BOUNCE_STRENGTH,
    DEFAULT_MODEL_VIEWER_CYCLORAMA_COLOR,
    DEFAULT_MODEL_VIEWER_CYCLORAMA_GLOSSINESS,
    DEFAULT_MODEL_VIEWER_CYCLORAMA_REFLECTION_STRENGTH,
    DEFAULT_MODEL_VIEWER_PRESENTATION_SETUP,
    DEFAULT_MODEL_VIEWER_STUDIO_AMBIENT_LIGHT_INTENSITY,
    DEFAULT_MODEL_VIEWER_STUDIO_DIRECT_LIGHT_INTENSITY,
    isModelViewerCameraFov,
    isModelViewerCycloramaBounceStrength,
    isModelViewerCycloramaColor,
    isModelViewerCycloramaGlossiness,
    isModelViewerCycloramaReflectionStrength,
    isModelViewerPresentationSetup,
    isModelViewerStudioAmbientLightIntensity,
    isModelViewerStudioDirectLightIntensity,
    type ModelViewerPresentationSetup,
} from './Presentation/Setup';

export type { ModelViewerPreviewPostEffectsData } from './PostEffects/config';

export interface ModelViewerPreviewCameraData {
    fov: number
    position: CameraPosition
    target: CameraPosition
}

export interface ModelViewerPreviewLightingData {
    ambient_intensity: number
    direct_intensity: number
}

export interface ModelViewerPreviewCycloramaData {
    bounce_strength: number
    color: string
    glossiness: number
    reflection_strength: number
}

export interface ModelViewerPreviewPedestalData {
    material: ModelViewerPedestalMaterial
    shape: ModelViewerPedestal
}

export interface ModelViewerPreviewPresentationData {
    cyclorama: ModelViewerPreviewCycloramaData
    lights: ModelViewerPreviewLightingData
    pedestal: ModelViewerPreviewPedestalData
    setup: ModelViewerPresentationSetup
}

/**
 * Persisted/API presentation input. Each group is an override of the shared
 * defaults, so products only need to store the settings that make a preview
 * different.
 */
export interface ModelViewerPreviewPresentationInputData {
    cyclorama?: Partial<ModelViewerPreviewCycloramaData>
    lights?: Partial<ModelViewerPreviewLightingData>
    pedestal?: Partial<ModelViewerPreviewPedestalData>
    setup?: ModelViewerPresentationSetup
}

export interface ModelViewerPreviewDataInput {
    camera: ModelViewerPreviewCameraData
    model?: string
    name: string
    post_effects?: Partial<ModelViewerPreviewPostEffectsData>
    presentation?: ModelViewerPreviewPresentationInputData
}

/**
 * Complete scene configuration for one output image. Product, source texture,
 * output resolution, and supersampling remain request-level because every
 * preview in a render request shares them. External input is normalized into
 * this type before it reaches scene components.
 */
export interface ModelViewerPreviewData {
    camera: ModelViewerPreviewCameraData
    model?: string
    name: string
    post_effects: ModelViewerPreviewPostEffectsData
    presentation: ModelViewerPreviewPresentationData
}

const DEFAULT_PREVIEW_NAME = 'front';

const createZeroPosition = ():CameraPosition => ({x: 0, y: 0, z: 0});

export const createDefaultModelViewerPreviewData = (
    name = DEFAULT_PREVIEW_NAME,
):ModelViewerPreviewData => ({
    camera: {
        fov: DEFAULT_MODEL_VIEWER_CAMERA_FOV,
        position: createZeroPosition(),
        target: createZeroPosition(),
    },
    name,
    post_effects: {...DEFAULT_MODEL_VIEWER_POST_EFFECTS},
    presentation: {
        cyclorama: {
            bounce_strength: DEFAULT_MODEL_VIEWER_CYCLORAMA_BOUNCE_STRENGTH,
            color: DEFAULT_MODEL_VIEWER_CYCLORAMA_COLOR,
            glossiness: DEFAULT_MODEL_VIEWER_CYCLORAMA_GLOSSINESS,
            reflection_strength: DEFAULT_MODEL_VIEWER_CYCLORAMA_REFLECTION_STRENGTH,
        },
        lights: {
            ambient_intensity: DEFAULT_MODEL_VIEWER_STUDIO_AMBIENT_LIGHT_INTENSITY,
            direct_intensity: DEFAULT_MODEL_VIEWER_STUDIO_DIRECT_LIGHT_INTENSITY,
        },
        pedestal: {
            material: DEFAULT_MODEL_VIEWER_PEDESTAL_MATERIAL,
            shape: DEFAULT_MODEL_VIEWER_PEDESTAL,
        },
        setup: DEFAULT_MODEL_VIEWER_PRESENTATION_SETUP,
    },
});

const resolveCameraPosition = (
    position:CameraPosition|undefined,
):CameraPosition => (
    position
    && Number.isFinite(position.x)
    && Number.isFinite(position.y)
    && Number.isFinite(position.z)
        ? {...position}
        : createZeroPosition()
);

/**
 * Normalizes the public data boundary once so browser events and callers that
 * originate outside TypeScript cannot install invalid scene values.
 */
export const resolveModelViewerPreviewData = (
    previewData?:ModelViewerPreviewDataInput,
):ModelViewerPreviewData => {
    const defaults = createDefaultModelViewerPreviewData();
    const camera = previewData?.camera;
    const postEffects = previewData?.post_effects;
    const presentation = previewData?.presentation;
    const cyclorama = presentation?.cyclorama;
    const lights = presentation?.lights;
    const pedestal = presentation?.pedestal;

    return {
        camera: {
            fov: isModelViewerCameraFov(camera?.fov)
                ? camera.fov
                : defaults.camera.fov,
            position: resolveCameraPosition(camera?.position),
            target: resolveCameraPosition(camera?.target),
        },
        ...(isModelViewerModelId(previewData?.model)
            ? {model: previewData.model}
            : {}),
        name: typeof previewData?.name === 'string' && previewData.name !== ''
            ? previewData.name
            : defaults.name,
        post_effects: resolveModelViewerPostEffectsData(postEffects),
        presentation: {
            cyclorama: {
                bounce_strength: isModelViewerCycloramaBounceStrength(
                    cyclorama?.bounce_strength,
                )
                    ? cyclorama.bounce_strength
                    : defaults.presentation.cyclorama.bounce_strength,
                color: isModelViewerCycloramaColor(cyclorama?.color)
                    ? cyclorama.color
                    : defaults.presentation.cyclorama.color,
                glossiness: isModelViewerCycloramaGlossiness(
                    cyclorama?.glossiness,
                )
                    ? cyclorama.glossiness
                    : defaults.presentation.cyclorama.glossiness,
                reflection_strength: isModelViewerCycloramaReflectionStrength(
                    cyclorama?.reflection_strength,
                )
                    ? cyclorama.reflection_strength
                    : defaults.presentation.cyclorama.reflection_strength,
            },
            lights: {
                ambient_intensity: isModelViewerStudioAmbientLightIntensity(
                    lights?.ambient_intensity,
                )
                    ? lights.ambient_intensity
                    : defaults.presentation.lights.ambient_intensity,
                direct_intensity: isModelViewerStudioDirectLightIntensity(
                    lights?.direct_intensity,
                )
                    ? lights.direct_intensity
                    : defaults.presentation.lights.direct_intensity,
            },
            pedestal: {
                material: isModelViewerPedestalMaterial(pedestal?.material)
                    ? pedestal.material
                    : defaults.presentation.pedestal.material,
                shape: isModelViewerPedestal(pedestal?.shape)
                    ? pedestal.shape
                    : defaults.presentation.pedestal.shape,
            },
            setup: isModelViewerPresentationSetup(presentation?.setup)
                ? presentation.setup
                : defaults.presentation.setup,
        },
    };
};
